/**
 * Leep Chas — Planner Quiz
 * Web Component <leep-planner-quiz>
 * 15 perguntas de saude/fitness → captura de email/telefone → download do planner PDF.
 *
 * Tipos de pergunta suportados:
 *   number       — input numerico com unidade
 *   single       — selecao unica com auto-avanco
 *   multi_other  — selecao multipla com campo "Outro" opcional
 *   duration     — tres campos numericos (anos/meses/dias)
 *   conditional  — selecao onde uma opcao revela textarea
 *   text         — textarea livre (opcional)
 */

const PQ_CONFIG_CACHE = 'leep_planner_config_v1';
const PQ_STATE_KEY    = 'leep_planner_state';

async function loadPlannerConfig() {
  try {
    const cached = sessionStorage.getItem(PQ_CONFIG_CACHE);
    if (cached) return JSON.parse(cached);
    const url = window.LEEP_PLANNER_CONFIG_URL;
    if (!url) throw new Error('LEEP_PLANNER_CONFIG_URL not set');
    const res  = await fetch(url);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const text = await res.text();
    sessionStorage.setItem(PQ_CONFIG_CACHE, text);
    return JSON.parse(text);
  } catch (e) {
    console.error('[PlannerQuiz] Config load failed:', e);
    return null;
  }
}

class LeepPlannerQuiz extends HTMLElement {
  constructor() {
    super();
    this._config = null;
    this._state  = null;
  }

  async connectedCallback() {
    this._config = await loadPlannerConfig();
    if (!this._config) return;

    this.webhookUrl   = this.dataset.webhookUrl   || '';
    this.plannerUrl   = this._config.planner_url  || '';

    this._state = this._loadState() || this._initState();

    if (this._state.completed) {
      this._state.screen = 'download';
    }

    this._render();
  }

  // ─── State ───────────────────────────────────────────────────────────────

  _initState() {
    return {
      quiz_id:      this._uuid(),
      started_at:   new Date().toISOString(),
      screen:       'welcome',
      current_step: 0,
      answers:      {},
      customer:     null,
      completed:    false,
    };
  }

  _loadState() {
    try {
      const raw = localStorage.getItem(PQ_STATE_KEY);
      if (!raw) return null;
      const s   = JSON.parse(raw);
      const age = Date.now() - new Date(s.started_at).getTime();
      if (age > 30 * 24 * 60 * 60 * 1000) return null;
      return s;
    } catch { return null; }
  }

  _saveState() {
    try { localStorage.setItem(PQ_STATE_KEY, JSON.stringify(this._state)); } catch {}
  }

  _uuid() {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
      const r = Math.random() * 16 | 0;
      return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
    });
  }

  // ─── Render ──────────────────────────────────────────────────────────────

  _render(direction = 'forward') {
    let html = '';
    switch (this._state.screen) {
      case 'welcome':  html = this._tplWelcome();  break;
      case 'question': html = this._tplQuestion(); break;
      case 'capture':  html = this._tplCapture();  break;
      case 'download': html = this._tplDownload(); break;
      default:         html = this._tplWelcome();
    }
    this.innerHTML = html;
    this._bindEvents();
    this._animateIn(direction);
  }

  _transition(mutate, direction = 'forward') {
    const screen = this.querySelector('.pq-screen');
    if (screen && window.gsap) {
      gsap.to(screen, {
        opacity: 0, y: direction === 'forward' ? -24 : 24,
        duration: 0.22, ease: 'power2.in',
        onComplete: () => { mutate(); this._render(direction); },
      });
    } else {
      mutate();
      this._render(direction);
    }
  }

  _animateIn(direction) {
    const screen = this.querySelector('.pq-screen');
    if (!screen || !window.gsap) return;
    gsap.fromTo(screen,
      { opacity: 0, y: direction === 'forward' ? 28 : -28 },
      { opacity: 1, y: 0, duration: 0.38, ease: 'power3.out' }
    );
  }

  // ─── Screen templates ─────────────────────────────────────────────────────

  _tplWelcome() {
    return `
      <div class="pq-screen pq-welcome">
        <div class="pq-welcome__inner">
          <span class="pq-badge">Planner Gratuito</span>
          <h1 class="pq-welcome__title">Monte seu plano de saude personalizado</h1>
          <p class="pq-welcome__sub">
            15 perguntas rapidas. Ao final, voce recebe um planner exclusivo e gratuito para organizar sua jornada.
          </p>
          <div class="pq-welcome__preview" aria-hidden="true">
            <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" style="color:var(--color-primary)">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
              <line x1="16" y1="13" x2="8" y2="13"/>
              <line x1="16" y1="17" x2="8" y2="17"/>
              <polyline points="10 9 9 9 8 9"/>
            </svg>
          </div>
          <button class="pq-btn pq-btn--primary pq-welcome__cta" data-action="start" autofocus>
            COMECAR AGORA
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M5 12h14M12 5l7 7-7 7"/>
            </svg>
          </button>
          <ul class="pq-welcome__pills">
            <li>100% Gratis</li>
            <li>Sem compromisso</li>
            <li>Download instantaneo</li>
          </ul>
        </div>
      </div>
    `;
  }

  _tplQuestion() {
    const q = this._config.questions[this._state.current_step];
    switch (q.type) {
      case 'number':      return this._tplNumber(q);
      case 'single':      return this._tplSingle(q);
      case 'multi_other': return this._tplMultiOther(q);
      case 'duration':    return this._tplDuration(q);
      case 'conditional': return this._tplConditional(q);
      case 'text':        return this._tplText(q);
      default:            return this._tplSingle(q);
    }
  }

  _tplNav(step, total) {
    return `
      <div class="pq-nav">
        <span class="pq-nav__step">Passo ${step} de ${total}</span>
        <button class="pq-back" data-action="prev" aria-label="Voltar">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
          Voltar
        </button>
      </div>
    `;
  }

  _tplProgress(current, total) {
    const pct = total > 0 ? Math.round((current / total) * 100) : 0;
    return `
      <div class="pq-progress" role="progressbar" aria-valuenow="${pct}" aria-valuemin="0" aria-valuemax="100" aria-label="Progresso: ${pct}%">
        <div class="pq-progress__bar" style="width:${pct}%"></div>
      </div>
    `;
  }

  // ── number ───────────────────────────────────────────────────────────────

  _tplNumber(q) {
    const total   = this._config.questions.length;
    const current = this._state.current_step;
    const saved   = this._state.answers[q.id] || '';
    return `
      <div class="pq-screen pq-question pq-question--number">
        ${this._tplProgress(current, total)}
        ${this._tplNav(current + 1, total)}
        <h2 class="pq-question__title">${q.title}</h2>
        <div class="pq-number-wrap">
          <input
            id="pq-input-${q.id}"
            type="number"
            class="pq-number-input"
            min="${q.min}" max="${q.max}"
            placeholder="${q.placeholder}"
            value="${this._esc(String(saved))}"
            inputmode="numeric"
            aria-label="${q.title}"
          >
          <span class="pq-number-unit">${q.unit}</span>
        </div>
        <span class="pq-field-error" id="pq-err-${q.id}" role="alert" hidden></span>
        <button class="pq-btn pq-btn--primary pq-continue" data-action="continue-number" data-question="${q.id}" data-min="${q.min}" data-max="${q.max}">
          CONTINUAR
        </button>
      </div>
    `;
  }

  // ── single ───────────────────────────────────────────────────────────────

  _tplSingle(q) {
    const total   = this._config.questions.length;
    const current = this._state.current_step;
    const saved   = this._state.answers[q.id];

    const options = q.options.map(opt => {
      const active = saved === opt.value;
      return `
        <button
          class="pq-option${active ? ' is-selected' : ''}"
          data-action="answer-single"
          data-question="${q.id}"
          data-value="${opt.value}"
          aria-pressed="${active}"
        >
          ${opt.emoji ? `<span class="pq-option__emoji" aria-hidden="true">${opt.emoji}</span>` : ''}
          <span class="pq-option__body">
            <span class="pq-option__label">${opt.label}</span>
            ${opt.desc ? `<span class="pq-option__desc">${opt.desc}</span>` : ''}
          </span>
          <span class="pq-option__check" aria-hidden="true">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
          </span>
        </button>
      `;
    }).join('');

    return `
      <div class="pq-screen pq-question">
        ${this._tplProgress(current, total)}
        ${this._tplNav(current + 1, total)}
        <h2 class="pq-question__title">${q.title}</h2>
        ${q.subtitle ? `<p class="pq-question__subtitle">${q.subtitle}</p>` : ''}
        <div class="pq-options" role="group" aria-label="Opcoes da pergunta ${current + 1}">
          ${options}
        </div>
      </div>
    `;
  }

  // ── multi_other ──────────────────────────────────────────────────────────

  _tplMultiOther(q) {
    const total   = this._config.questions.length;
    const current = this._state.current_step;
    const saved   = this._state.answers[q.id]?.values || [];
    const outroTxt = this._state.answers[`${q.id}_outro`] || '';
    const outroOn  = saved.includes('outro');

    const options = q.options.map(opt => {
      const active = saved.includes(opt.value);
      return `
        <button
          class="pq-option pq-option--chip${active ? ' is-selected' : ''}"
          data-action="toggle-multi-other"
          data-question="${q.id}"
          data-value="${opt.value}"
          data-is-other="${opt.is_other ? 'true' : 'false'}"
          aria-pressed="${active}"
        >
          <span class="pq-option__label">${opt.label}</span>
          <span class="pq-option__check" aria-hidden="true">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
          </span>
        </button>
      `;
    }).join('');

    return `
      <div class="pq-screen pq-question">
        ${this._tplProgress(current, total)}
        ${this._tplNav(current + 1, total)}
        <h2 class="pq-question__title">${q.title}</h2>
        ${q.subtitle ? `<p class="pq-question__subtitle">${q.subtitle}</p>` : ''}
        <div class="pq-options pq-options--chips" role="group" aria-label="Opcoes da pergunta ${current + 1}">
          ${options}
        </div>
        <div class="pq-outro-field${outroOn ? '' : ' pq-hidden'}" id="pq-outro-wrap-${q.id}">
          <label class="pq-field-label" for="pq-outro-${q.id}">Qual exercicio?</label>
          <input
            type="text"
            id="pq-outro-${q.id}"
            class="pq-text-input pq-text-input--sm"
            placeholder="${q.options.find(o => o.is_other)?.other_placeholder || 'Descreva'}"
            value="${this._esc(outroTxt)}"
            aria-label="Descreva o exercicio"
          >
        </div>
        <button
          class="pq-btn pq-btn--primary pq-continue"
          data-action="continue-multi-other"
          data-question="${q.id}"
          ${saved.length === 0 ? 'disabled' : ''}
        >
          CONTINUAR
        </button>
      </div>
    `;
  }

  // ── duration ─────────────────────────────────────────────────────────────

  _tplDuration(q) {
    const total   = this._config.questions.length;
    const current = this._state.current_step;
    const saved   = this._state.answers[q.id] || {};

    const fields = q.fields.map(f => `
      <div class="pq-duration-field">
        <input
          type="number"
          id="pq-dur-${q.id}-${f.id}"
          class="pq-duration-input"
          min="${f.min}" max="${f.max}"
          placeholder="${f.placeholder}"
          value="${saved[f.id] !== undefined ? saved[f.id] : ''}"
          inputmode="numeric"
          aria-label="${f.label}"
        >
        <label class="pq-duration-label" for="pq-dur-${q.id}-${f.id}">${f.label}</label>
      </div>
    `).join('');

    return `
      <div class="pq-screen pq-question pq-question--duration">
        ${this._tplProgress(current, total)}
        ${this._tplNav(current + 1, total)}
        <h2 class="pq-question__title">${q.title}</h2>
        <div class="pq-duration-wrap">${fields}</div>
        <span class="pq-field-error" id="pq-err-${q.id}" role="alert" hidden></span>
        <button class="pq-btn pq-btn--primary pq-continue" data-action="continue-duration" data-question="${q.id}">
          CONTINUAR
        </button>
      </div>
    `;
  }

  // ── conditional ──────────────────────────────────────────────────────────

  _tplConditional(q) {
    const total   = this._config.questions.length;
    const current = this._state.current_step;
    const saved   = this._state.answers[q.id] || {};
    const simOn   = saved.value === 'sim';

    const options = q.options.map(opt => {
      const active = saved.value === opt.value;
      return `
        <button
          class="pq-option${active ? ' is-selected' : ''}"
          data-action="select-conditional"
          data-question="${q.id}"
          data-value="${opt.value}"
          data-is-conditional="${opt.is_conditional ? 'true' : 'false'}"
          data-text-placeholder="${this._esc(opt.text_placeholder || '')}"
          aria-pressed="${active}"
        >
          <span class="pq-option__label">${opt.label}</span>
          <span class="pq-option__check" aria-hidden="true">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
          </span>
        </button>
      `;
    }).join('');

    return `
      <div class="pq-screen pq-question pq-question--conditional">
        ${this._tplProgress(current, total)}
        ${this._tplNav(current + 1, total)}
        <h2 class="pq-question__title">${q.title}</h2>
        <div class="pq-options" role="group" aria-label="Opcoes da pergunta ${current + 1}">
          ${options}
        </div>
        <div class="pq-conditional-text${simOn ? '' : ' pq-hidden'}" id="pq-conditional-wrap-${q.id}">
          <label class="pq-field-label" for="pq-cond-text-${q.id}">Descreva:</label>
          <textarea
            id="pq-cond-text-${q.id}"
            class="pq-textarea"
            rows="3"
            placeholder="${this._esc(q.options.find(o => o.is_conditional)?.text_placeholder || '')}"
            aria-label="Descricao da condicao"
          >${this._esc(saved.text || '')}</textarea>
        </div>
        ${q.disclaimer ? `<p class="pq-disclaimer">${q.disclaimer}</p>` : ''}
        <button
          class="pq-btn pq-btn--primary pq-continue${simOn ? '' : ' pq-hidden'}"
          id="pq-cond-continue-${q.id}"
          data-action="continue-conditional"
          data-question="${q.id}"
        >
          CONTINUAR
        </button>
      </div>
    `;
  }

  // ── text ─────────────────────────────────────────────────────────────────

  _tplText(q) {
    const total   = this._config.questions.length;
    const current = this._state.current_step;
    const saved   = this._state.answers[q.id] || '';
    return `
      <div class="pq-screen pq-question pq-question--text">
        ${this._tplProgress(current, total)}
        ${this._tplNav(current + 1, total)}
        <h2 class="pq-question__title">${q.title}</h2>
        ${q.optional ? '<p class="pq-question__subtitle">(opcional)</p>' : ''}
        <textarea
          id="pq-textarea-${q.id}"
          class="pq-textarea"
          rows="4"
          placeholder="${this._esc(q.placeholder || '')}"
          aria-label="${q.title}"
        >${this._esc(saved)}</textarea>
        <button class="pq-btn pq-btn--primary pq-continue" data-action="continue-text" data-question="${q.id}">
          CONTINUAR
        </button>
      </div>
    `;
  }

  // ── capture ───────────────────────────────────────────────────────────────

  _tplCapture() {
    const c = this._state.customer || {};
    return `
      <div class="pq-screen pq-capture">
        <span class="pq-badge">Quase la!</span>
        <h2 class="pq-capture__title">Seu Planner esta pronto.</h2>
        <p class="pq-capture__sub">
          Informe seus dados abaixo para receber o planner personalizado gratuitamente.
        </p>
        <form class="pq-capture__form" data-form="planner-capture" novalidate>
          <div class="pq-field">
            <label class="pq-field-label" for="pq-cap-name">Seu nome</label>
            <input
              id="pq-cap-name" name="name" type="text"
              class="pq-text-input" placeholder="Maria"
              autocomplete="given-name"
              value="${this._esc(c.first_name || '')}"
            >
          </div>
          <div class="pq-field">
            <label class="pq-field-label" for="pq-cap-email">
              E-mail <span class="pq-required" aria-label="obrigatorio">*</span>
            </label>
            <input
              id="pq-cap-email" name="email" type="email"
              class="pq-text-input" placeholder="maria@email.com"
              autocomplete="email" required
              value="${this._esc(c.email || '')}"
            >
            <span class="pq-field-error" id="pq-cap-email-err" role="alert" aria-live="assertive" hidden></span>
          </div>
          <div class="pq-field">
            <label class="pq-field-label" for="pq-cap-phone">
              Telefone / WhatsApp <span class="pq-required" aria-label="obrigatorio">*</span>
            </label>
            <input
              id="pq-cap-phone" name="phone" type="tel"
              class="pq-text-input" placeholder="+55 11 99999-9999"
              autocomplete="tel" required
              value="${this._esc(c.phone || '')}"
            >
            <span class="pq-field-error" id="pq-cap-phone-err" role="alert" aria-live="assertive" hidden></span>
          </div>
          <label class="pq-checkbox">
            <input type="checkbox" name="accepts_marketing" checked>
            <span>Quero receber dicas de saude e bem-estar</span>
          </label>
          <button type="submit" class="pq-btn pq-btn--primary pq-capture__submit">
            BAIXAR MEU PLANNER GRATIS
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="7 10 12 15 17 10"/>
              <line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
          </button>
          <p class="pq-capture__disclaimer">
            Seus dados estao seguros. Nao compartilhamos com terceiros.
          </p>
        </form>
      </div>
    `;
  }

  // ── download ──────────────────────────────────────────────────────────────

  _tplDownload() {
    const name = this._state.customer?.first_name || '';
    return `
      <div class="pq-screen pq-download">
        <div class="pq-download__icon" aria-hidden="true">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" style="color:var(--color-primary)">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
            <polyline points="22 4 12 14.01 9 11.01"/>
          </svg>
        </div>
        <h2 class="pq-download__title">
          ${name ? `${this._esc(name)}, seu planner esta pronto!` : 'Seu planner esta pronto!'}
        </h2>
        <p class="pq-download__sub">
          Clique no botao abaixo para baixar seu planner personalizado gratuitamente.
        </p>
        <a
          href="${this._esc(this.plannerUrl)}"
          target="_blank"
          rel="noopener"
          class="pq-btn pq-btn--primary pq-download__btn"
          download
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
            <polyline points="7 10 12 15 17 10"/>
            <line x1="12" y1="15" x2="12" y2="3"/>
          </svg>
          BAIXAR PLANNER (PDF)
        </a>
        <p class="pq-download__hint">
          O arquivo abre em uma nova aba. Salve para usar sempre que quiser.
        </p>
        <button class="pq-restart" data-action="restart">Refazer o quiz</button>
      </div>
    `;
  }

  // ─── Event binding ────────────────────────────────────────────────────────

  _bindEvents() {
    this.querySelectorAll('[data-action]').forEach(el => {
      el.addEventListener('click', e => this._onAction(e.currentTarget));
    });
    const form = this.querySelector('[data-form="planner-capture"]');
    if (form) form.addEventListener('submit', e => { e.preventDefault(); this._handleCapture(form); });
  }

  _onAction(el) {
    const action = el.dataset.action;

    // ── start ──────────────────────────────────────────────────────────────
    if (action === 'start') {
      this._transition(() => {
        this._state.screen       = 'question';
        this._state.current_step = 0;
        this._saveState();
      });
      return;
    }

    // ── prev ───────────────────────────────────────────────────────────────
    if (action === 'prev') {
      this._transition(() => {
        if (this._state.screen === 'capture') {
          this._state.screen       = 'question';
          this._state.current_step = this._config.questions.length - 1;
        } else if (this._state.current_step > 0) {
          this._state.current_step--;
        } else {
          this._state.screen = 'welcome';
        }
        this._saveState();
      }, 'back');
      return;
    }

    // ── single auto-advance ────────────────────────────────────────────────
    if (action === 'answer-single') {
      const { question, value } = el.dataset;
      this._state.answers[question] = value;
      el.classList.add('is-selected');
      el.setAttribute('aria-pressed', 'true');
      this._pulse(el);
      this._saveState();
      setTimeout(() => this._advanceQuestion(), 300);
      return;
    }

    // ── continue number ────────────────────────────────────────────────────
    if (action === 'continue-number') {
      const { question, min, max } = el.dataset;
      const input = this.querySelector(`#pq-input-${question}`);
      const errEl = this.querySelector(`#pq-err-${question}`);
      const val   = input?.value.trim();
      const num   = parseFloat(val);

      if (!val || isNaN(num) || num < parseFloat(min) || num > parseFloat(max)) {
        if (errEl) { errEl.textContent = `Informe um valor entre ${min} e ${max}.`; errEl.hidden = false; }
        input?.focus();
        return;
      }
      if (errEl) errEl.hidden = true;
      this._state.answers[question] = num;
      this._saveState();
      this._advanceQuestion();
      return;
    }

    // ── toggle multi_other ─────────────────────────────────────────────────
    if (action === 'toggle-multi-other') {
      const { question, value, isOther } = el.dataset;
      const arr = [...(this._state.answers[question]?.values || [])];
      const idx = arr.indexOf(value);
      if (idx === -1) { arr.push(value); el.classList.add('is-selected'); el.setAttribute('aria-pressed', 'true'); this._pulse(el); }
      else            { arr.splice(idx, 1); el.classList.remove('is-selected'); el.setAttribute('aria-pressed', 'false'); }

      this._state.answers[question] = { ...(this._state.answers[question] || {}), values: arr };
      this._saveState();

      // Show/hide "Outro" text field
      const outroWrap = this.querySelector(`#pq-outro-wrap-${question}`);
      if (isOther === 'true' && outroWrap) {
        outroWrap.classList.toggle('pq-hidden', !arr.includes('outro'));
        if (arr.includes('outro')) outroWrap.querySelector('input')?.focus();
      }

      // Enable/disable continue
      const continueBtn = this.querySelector('.pq-continue');
      if (continueBtn) continueBtn.disabled = arr.length === 0;
      return;
    }

    // ── continue multi_other ───────────────────────────────────────────────
    if (action === 'continue-multi-other') {
      const { question } = el.dataset;
      const outroInput   = this.querySelector(`#pq-outro-${question}`);
      const arr          = this._state.answers[question]?.values || [];

      if (arr.includes('outro') && outroInput) {
        this._state.answers[`${question}_outro`] = outroInput.value.trim();
      }
      this._saveState();
      this._advanceQuestion();
      return;
    }

    // ── continue duration ──────────────────────────────────────────────────
    if (action === 'continue-duration') {
      const { question } = el.dataset;
      const q            = this._config.questions[this._state.current_step];
      const errEl        = this.querySelector(`#pq-err-${question}`);
      const result       = {};
      let   total        = 0;

      for (const f of q.fields) {
        const input = this.querySelector(`#pq-dur-${question}-${f.id}`);
        const val   = parseInt(input?.value || '0', 10);
        result[f.id] = isNaN(val) ? 0 : Math.max(f.min, Math.min(f.max, val));
        total += result[f.id];
      }

      if (total === 0) {
        if (errEl) { errEl.textContent = 'Informe pelo menos um valor acima de zero.'; errEl.hidden = false; }
        return;
      }
      if (errEl) errEl.hidden = true;

      this._state.answers[question] = result;
      this._saveState();
      this._advanceQuestion();
      return;
    }

    // ── conditional select ─────────────────────────────────────────────────
    if (action === 'select-conditional') {
      const { question, value, isConditional, textPlaceholder } = el.dataset;

      // Mark selection visually (no re-render)
      this.querySelectorAll(`[data-question="${question}"]`).forEach(btn => {
        const active = btn.dataset.value === value;
        btn.classList.toggle('is-selected', active);
        btn.setAttribute('aria-pressed', active);
      });
      this._state.answers[question] = { value, text: null };
      this._saveState();

      const textWrap    = this.querySelector(`#pq-conditional-wrap-${question}`);
      const continueBtn = this.querySelector(`#pq-cond-continue-${question}`);
      const isCondit    = isConditional === 'true';

      if (textWrap)    textWrap.classList.toggle('pq-hidden', !isCondit);
      if (continueBtn) continueBtn.classList.toggle('pq-hidden', !isCondit);

      if (!isCondit) {
        // Auto-advance for "Nenhuma"
        this._pulse(el);
        setTimeout(() => this._advanceQuestion(), 300);
      } else {
        const ta = textWrap?.querySelector('textarea');
        if (ta) { ta.placeholder = textPlaceholder || ''; ta.focus(); }
      }
      return;
    }

    // ── continue conditional ───────────────────────────────────────────────
    if (action === 'continue-conditional') {
      const { question } = el.dataset;
      const ta           = this.querySelector(`#pq-cond-text-${question}`);
      this._state.answers[question] = {
        ...(this._state.answers[question] || {}),
        text: ta?.value.trim() || null,
      };
      this._saveState();
      this._advanceQuestion();
      return;
    }

    // ── continue text ──────────────────────────────────────────────────────
    if (action === 'continue-text') {
      const { question } = el.dataset;
      const ta           = this.querySelector(`#pq-textarea-${question}`);
      this._state.answers[question] = ta?.value.trim() || '';
      this._saveState();
      this._advanceQuestion();
      return;
    }

    // ── restart ────────────────────────────────────────────────────────────
    if (action === 'restart') {
      localStorage.removeItem(PQ_STATE_KEY);
      this._state = this._initState();
      this._transition(() => {});
      return;
    }
  }

  _advanceQuestion() {
    const nextStep = this._state.current_step + 1;
    this._transition(() => {
      if (nextStep >= this._config.questions.length) {
        this._state.screen = 'capture';
      } else {
        this._state.current_step = nextStep;
      }
      this._saveState();
    });
  }

  _pulse(el) {
    if (!window.gsap) return;
    gsap.timeline()
      .to(el, { scale: 1.04, duration: 0.12, ease: 'power1.out' })
      .to(el, { scale: 1,    duration: 0.18, ease: 'power2.out' });
  }

  // ─── Capture ─────────────────────────────────────────────────────────────

  _handleCapture(form) {
    const emailEl = form.querySelector('[name="email"]');
    const phoneEl = form.querySelector('[name="phone"]');
    const emailEr = form.querySelector('#pq-cap-email-err');
    const phoneEr = form.querySelector('#pq-cap-phone-err');
    let valid = true;

    const email = emailEl?.value.trim();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      if (emailEr) { emailEr.textContent = 'Informe um e-mail valido.'; emailEr.hidden = false; }
      emailEl?.focus();
      valid = false;
    } else if (emailEr) emailEr.hidden = true;

    const phone = phoneEl?.value.trim();
    if (!phone || phone.replace(/\D/g, '').length < 10) {
      if (phoneEr) { phoneEr.textContent = 'Informe um telefone valido.'; phoneEr.hidden = false; }
      if (valid) phoneEl?.focus();
      valid = false;
    } else if (phoneEr) phoneEr.hidden = true;

    if (!valid) return;

    this._state.customer = {
      email,
      phone,
      first_name:        form.querySelector('[name="name"]')?.value.trim() || '',
      accepts_marketing: form.querySelector('[name="accepts_marketing"]')?.checked || false,
    };
    this._state.completed = true;
    this._saveState();

    // Fire integrations
    this._sendWebhook();
    this._pushDataLayer();

    // Show download screen and auto-open PDF
    this._transition(() => { this._state.screen = 'download'; });
    setTimeout(() => {
      if (this.plannerUrl) window.open(this.plannerUrl, '_blank', 'noopener');
    }, 800);
  }

  // ─── Integrations ────────────────────────────────────────────────────────

  async _sendWebhook() {
    if (!this.webhookUrl) return;
    try {
      await fetch(this.webhookUrl, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          event:        'planner_quiz_completed',
          quiz_id:      this._state.quiz_id,
          completed_at: new Date().toISOString(),
          customer:     this._state.customer,
          answers:      this._state.answers,
          metadata: {
            user_agent:   navigator.userAgent,
            utm_source:   new URLSearchParams(location.search).get('utm_source'),
            utm_medium:   new URLSearchParams(location.search).get('utm_medium'),
            utm_campaign: new URLSearchParams(location.search).get('utm_campaign'),
            referrer:     document.referrer,
          },
        }),
      });
    } catch (e) {
      console.warn('[PlannerQuiz] Webhook failed:', e);
    }
  }

  _pushDataLayer() {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event:       'planner_quiz_completed',
      quiz_id:     this._state.quiz_id,
      user_email:  this._state.customer?.email,
      user_phone:  this._state.customer?.phone,
    });
  }

  // ─── Helpers ─────────────────────────────────────────────────────────────

  _esc(str) {
    return String(str ?? '')
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }
}

customElements.define('leep-planner-quiz', LeepPlannerQuiz);
